import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import './QuantitySelector.less';
import {ButtonIcon} from '@homeaway/react-buttons';
import {MINUS__16, PLUS__16} from '@homeaway/svg-defs';

function QuantitySelector({
    className,
    decreaseLabel,
    disableInput,
    id,
    increaseLabel,
    label,
    labelData,
    max,
    min,
    onDecrement,
    onIncrement,
    step,
    value,
    ...rest
}) {
    const selectorClasses = classNames('quantity-selector', className);
    const isLabelComponent = typeof label === 'function';
    const LabelComponent = label;
    let textInput;

    function stepUp() {
        const newValue = Number(value) + Number(step);

        if (newValue <= max) {
            onIncrement(newValue);
        }
    }

    function stepDown() {
        const newValue = Number(value) - Number(step);

        if (newValue >= min) {
            onDecrement(newValue);
        }
    }

    // handle a11y users editing the text using AT
    function handleA11y() {
        let newValue = textInput.value || 0;

        // Remove possible leading zeros from input value
        if (newValue) {
            newValue = parseInt(newValue, 10);
            textInput.value = newValue;
        }

        if (newValue !== value && newValue >= min && newValue <= max) {
            if (newValue > value) {
                onIncrement(newValue);
            } else {
                onDecrement(newValue);
            }
        }
    }

    return (
        <div className={selectorClasses} {...rest}>
            <label
                aria-live="polite"
                className="quantity-selector__label text-muted"
                htmlFor={id}
            >
                {isLabelComponent ? (
                    <span className="quantity-selector__custom-label">
                        <LabelComponent data={labelData} />
                    </span>
                ) : (
                    label
                )}
                <input
                    disabled={disableInput}
                    type="number"
                    min={min}
                    max={max}
                    ref={(ref) => (textInput = ref)}
                    className="quantity-selector__input"
                    id={id}
                    value={value}
                    onChange={handleA11y}
                />
                <div className="quantity-selector__focus-indicator bg-brand" />
            </label>
            <ButtonIcon
                disabled={value <= min}
                label={decreaseLabel}
                onClick={stepDown}
                className="quantity-selector__btn quantity-selector__btn--decrease"
                svgMarkup={MINUS__16}
                circle
            />
            <ButtonIcon
                disabled={value >= max}
                label={increaseLabel}
                onClick={stepUp}
                className="quantity-selector__btn quantity-selector__btn--increase"
                svgMarkup={PLUS__16}
                circle
            />
        </div>
    );
}

QuantitySelector.propTypes = {
    /** Label for the Decrement Button, required for a11y */
    decreaseLabel: PropTypes.string.isRequired,
    /** Boolean to disable the focus/underline/keyboard input functionality of the input field */
    disableInput: PropTypes.bool,
    /** id attribute for QuantitySelector, required for a11y */
    id: PropTypes.string.isRequired,
    /** Label for the Increment Button, required for a11y */
    increaseLabel: PropTypes.string.isRequired,
    /** Label for the Quantity Selector (ex: 0 Children); This can be a string, element, or component. Required for a11y. */
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
        PropTypes.func
    ]).isRequired,
    /** Optional data required by custom label component */
    labelData: PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        label: PropTypes.string
    }),
    /** max attribute for the number input */
    max: PropTypes.number,
    /** min attribute for the number input */
    min: PropTypes.number,
    /** event handler to fire when the number decreases */
    onDecrement: PropTypes.func.isRequired,
    /** event handler to fire when the number increases */
    onIncrement: PropTypes.func.isRequired,
    /** how much to increment/decrement per button click*/
    step: PropTypes.number,
    /** value for the number input */
    value: PropTypes.oneOfType([
        PropTypes.number.isRequired,
        PropTypes.string.isRequired
    ])
};

QuantitySelector.defaultProps = {
    disableInput: false,
    max: 20,
    min: 0,
    onDecrement: /* istanbul ignore next */ () => {},
    onIncrement: /* istanbul ignore next */ () => {},
    step: 1,
    value: 0
};

export default QuantitySelector;
